import React, { useEffect } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { desktopSizes, mobileSizes } from "../../util/sizes";
import en from "../../util/locales/en";
import ar from "../../util/locales/ar";
import { useCarFilters } from "../../hooks/car/useCarFilters";
import { useModels } from "../../hooks/car/useModels";
import BrandSection from "./BrandSection";
import PriceSection from "./PriceSection";
import FilterSection from "./FilterSection";
import YearSection from "./YearSection";

const SidebarFilter = ({
  homeBrand,
  homeShape,
  brands,
  selectedBrands,
  setSelectedBrands,
  selectedEngineCapacities,
  setSelectedEngineCapacities,
  selectedFuelTypes,
  setSelectedFuelTypes,
  selectedTransmissions,
  setSelectedTransmissions,
  selectedShapes,
  setSelectedShapes,
  selectedModels,
  setSelectedModels,
  toggleTransmissionCheckbox,
  toggleShapesCheckbox,
  toggleFuelTypesCheckbox,
  toggleEngineCapacitiesCheckbox,
  toggleModelCheckbox,
  clearFilters,
  isCleared,
  maxPrice,
  setMaxPrice,
  minPrice,
  setMinPrice,
  maxYear,
  setMaxYear,
  minYear,
  setMinYear,
  handleFilter,
  getAllBrands,
  brandsCount,
  isMobile,
  collapseBrands,
  setCollapseBrands,
  searchKey,
  setSearchKey,
  sortBrands,
  brandLoading,
  ...props
}) => {
  const language = useSelector((state) => state.language);
  const t = language === "en" ? en : ar;

  const { shapes, transmissions, fuelTypes, engineCapacities } =
    useCarFilters();

  const {
    getModelsForBrand,
    isLoadingForBrand,
    isCollapsedForBrand,
    toggleCollapseForBrand,
  } = useModels(selectedBrands);

  // Handle scroll to brand/shape when coming from home
  useEffect(() => {
    if (selectedBrands.size === 1) {
      if (homeBrand !== undefined && homeBrand !== null) {
        const [first] = selectedBrands;
        if (first.id === homeBrand) {
          const element = document.getElementById(homeBrand);
          if (element) {
            element.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
              inline: "start",
            });
          }
        }
      }
    } else if (selectedShapes.size === 1) {
      if (homeShape !== undefined && homeShape !== null) {
        const [first] = selectedShapes;
        if (first === homeShape) {
          const element = document.getElementById(`s-${homeShape}`);
          if (element) {
            element.scrollIntoView({
              behavior: "smooth",
              inline: "start",
            });
          }
        }
      }
    }
  }, [selectedBrands, selectedShapes]);

  const handleBrandToggle = (brand) => {
    console.log("BBBBrand ####", brand);

    const isBrandSelected = [...selectedBrands].some(
      (selectedBrand) => selectedBrand.id === brand.id
    );

    if (isBrandSelected) {
      console.log("REMOVING BRAND ####", brand);

      // Remove brand by filtering out the one with matching ID
      const updatedBrands = new Set(
        [...selectedBrands].filter(
          (selectedBrand) => selectedBrand.id !== brand.id
        )
      );
      setSelectedBrands(updatedBrands);

      // Remove associated models
      const updatedModels = new Set(
        [...selectedModels].filter((model) => model.brand !== brand.id)
      );
      setSelectedModels(updatedModels);
    } else {
      console.log("ADDING BRAND ####", brand);
      // Add brand to selection
      setSelectedBrands((prev) => new Set([...prev, brand]));
    }
  };

  return (
    <StyledSidebar collapseBrands={collapseBrands} language={language}>
      <div className="sidebar-header">
        <div className="title">{t.carsFilter.detailedSearch}</div>
        <button className="clear" onClick={clearFilters} disabled={isCleared()}>
          {isMobile ? t.carsFilter.clear.split(" ")[0] : t.carsFilter.clear}
        </button>
      </div>

      <div className="filters">
        {/* Brand Section */}
        <BrandSection
          brands={brands}
          selectedBrands={selectedBrands}
          collapseBrands={collapseBrands}
          setCollapseBrands={setCollapseBrands}
          brandsCount={brandsCount}
          searchKey={searchKey}
          setSearchKey={setSearchKey}
          getAllBrands={getAllBrands}
          sortBrands={sortBrands}
          onBrandToggle={handleBrandToggle}
          getModelsForBrand={getModelsForBrand}
          isLoadingForBrand={isLoadingForBrand}
          isCollapsedForBrand={isCollapsedForBrand}
          toggleCollapseForBrand={toggleCollapseForBrand}
          selectedModels={selectedModels}
          onModelToggle={toggleModelCheckbox}
          language={language}
          brandLoading={brandLoading}
          t={t}
        />

        {/* Year Section */}
        <YearSection
          minYear={minYear}
          setMinYear={setMinYear}
          maxYear={maxYear}
          setMaxYear={setMaxYear}
          t={t}
        />
        {/* Price Section */}
        <PriceSection
          minPrice={minPrice}
          setMinPrice={setMinPrice}
          maxPrice={maxPrice}
          setMaxPrice={setMaxPrice}
          t={t}
        />

        {/* Transmission Section */}
        <FilterSection
          title={t.carsFilter.transmission}
          items={transmissions}
          selectedItems={selectedTransmissions}
          onToggle={toggleTransmissionCheckbox}
          getLabel={(item) => t.transmissions[item] || item}
          idPrefix="t-"
        />

        {/* Body Shape Section */}
        <FilterSection
          title={t.carsFilter.bodyShape}
          items={shapes}
          selectedItems={selectedShapes}
          onToggle={toggleShapesCheckbox}
          getLabel={(item) =>
            language === "en" ? item : ar.body_types[item] || item
          }
          idPrefix="s-"
          sectionId="body-shapes"
        />

        {/* Fuel Type Section */}
        <FilterSection
          title={t.carsFilter.fuelType}
          items={fuelTypes}
          selectedItems={selectedFuelTypes}
          onToggle={toggleFuelTypesCheckbox}
          getLabel={(item) => (t.fuel_types ? t.fuel_types[item] : item)}
          idPrefix="f-"
        />

        {/* Engine Capacity Section */}
        <FilterSection
          title={t.carsFilter.engineCapacity}
          items={engineCapacities}
          selectedItems={selectedEngineCapacities}
          onToggle={toggleEngineCapacitiesCheckbox}
          getLabel={(item) => item + " CC"}
          idPrefix="e-"
          labelClassName="capacity-label"
        />
      </div>
    </StyledSidebar>
  );
};

const StyledSidebar = styled.div`
  direction: ${(props) => (props.language == "en" ? "ltr" : "rtl")};
  width: 25vw;
  background: #ffffff;
  border: 0.069vw solid #e4e6e8;
  border-radius: 0.278vw;
  padding: 2.222vw 0 1.667vw;
  .sidebar-header {
    padding: 0 1.111vw;
    margin-bottom: 1.5vw;
    display: flex;
    justify-content: space-between;
    align-items: center;
    .title {
      font-family: ${(props) =>
        props.language == "en" ? "Poppins" : "Almarai"};
      font-style: normal;
      font-weight: ${(props) => (props.language == "en" ? "600" : "700")};
      font-size: 1.389vw;
      line-height: 2.222vw;
      color: #0d0a19;

      @media (min-width: 1440px) {
        font-size: 20px;
        line-height: 32px;
      }
    }

    .clear {
      cursor: pointer;
      border: none;
      background: none;
      font-family: ${(props) =>
        props.language == "en" ? "Poppins" : "Almarai"};
      font-style: normal;
      font-weight: ${(props) => (props.language == "en" ? "600" : "700")};
      font-size: 1.111vw;
      line-height: 2.222vw;
      color: #eb0100;
      @media (min-width: 1440px) {
        font-size: 16px;
        line-height: 32px;
      }
    }
    .clear: disabled {
      opacity: 0.4;
    }
  }
  .filters {
    padding: 0 1.111vw;
    // height: 40vw;
    // height: calc(100vh - 22vw);
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 1.667vw;
    margin-bottom: 1.667vw;

    .filter-section {
      padding: 0 1.111vw 1.667vw 1.111vw;
      // border-bottom: 0.069vw solid #e4e6e8;
      .filter-title {
        font-family: ${(props) =>
          props.language == "en" ? "Poppins" : "Almarai"};
        font-style: normal;
        font-weight: ${(props) => (props.language == "en" ? "600" : "700")};
        font-size: 1.111vw;
        line-height: 2.222vw;
        color: #0D0A1;

        @media (min-width: 1440px) {
          font-size: 16px;
          line-height: 32px;
        }
      }
      .search-form {
        position: relative;
        margin-top: 1.111vw;
        .search-input {
          width: 22.778vw;
          height: 4.306vw;
          background: #ffffff;
          border: 0.069vw solid #e4e6e8;
          border-radius: 0.556vw;
          font-weight: 500;
          font-size: 1.111vw;
          line-height: 2.222vw;
          color: #0d0a19;
          padding: 1.111vw;
          padding-right: ${(props) => props.language == "en" && " 3.125vw"};
          padding-left: ${(props) => props.language == "ar" && " 3.125vw"};

          @media (min-width: 1440px) {
            font-size: 16px;
            line-height: 32px;
          }
        }
        .search-input::placeholder {
          font-family: ${(props) =>
            props.language == "en" ? "Poppins" : "Almarai"};
          font-style: normal;
          font-weight: ${(props) => (props.language == "en" ? "400" : "300")};
          font-size: 1.111vw;
          line-height: 2.222vw;
          color: #0d0a19;
          opacity: 0.5;

          @media (min-width: 1440px) {
            font-size: 16px;
            line-height: 32px;
          }
        }
        .search {
          position: absolute;
          border: none;
          background: none;
          left: ${(props) => props.language == "en" && "20vw"};
          right: ${(props) => props.language == "ar" && "20vw"};
          top: 1.319vw;
          svg {
            width: 1.667vw;
            height: 1.667vw;
            @media (min-width: 1440px) {
              width: 24px;
              height: 24px;
            }
          }
        }
      }
      .view-all {
        margin-top: 1.667vw;
        font-family: ${(props) =>
          props.language == "en" ? "Poppins" : "Almarai"};
        font-style: normal;
        font-weight: ${(props) => (props.language == "en" ? "600" : "700")};
        font-size: 1.111vw;
        line-height: 1.319vw;
        color: #154cd6;
        border: none;
        background: none;
        cursor: pointer;

        @media (min-width: 1440px) {
          font-size: 16px;
          line-height: 20px;
        }
      }
      .price-inputs {
        display: flex;
        align-items: center;
        gap: 0.556vw;
        margin-top: 1.111vw;
        .input-div {
          position: relative;

          input {
            background: #ffffff;
            border: 0.069vw solid #e4e6e8;
            border-radius: 0.556vw;
            width: 11.111vw;
            height: 4.306vw;
            padding: ${(props) =>
              props.language == "en"
                ? "1.528vw 3.125vw 1.528vw 1.111vw"
                : "1.528vw 1.111vw  1.528vw 3.125vw"};
            font-weight: 400;
            font-size: 1.111vw;
            line-height: 1.319vw;
            color: #0d0a19;

            @media (min-width: 1440px) {
              font-size: 16px;
              line-height: 20px;
            }
          }
          input::placeholder {
            font-family: ${(props) =>
              props.language == "en" ? "Poppins" : "Almarai"};
            font-style: normal;
            font-weight: ${(props) => (props.language == "en" ? "400" : "300")};
            font-size: 1.111vw;
            line-height: 1.319vw;
            color: #0d0a19;
            opacity: 0.5;
            @media (min-width: 1440px) {
              font-size: 16px;
              line-height: 20px;
            }
          }

          input::-webkit-outer-spin-button,
          input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }

          /* Firefox */
          input[type="number"] {
            -moz-appearance: textfield;
          }
          label {
            position: absolute;
            left: ${(props) => props.language == "en" && "8.333vw"};
            right: ${(props) => props.language == "ar" && "8.333vw"};
            top: 1.667vw;
            font-family: ${(props) =>
              props.language == "en" ? "Poppins" : "Almarai"};
            font-style: normal;
            font-weight: 500;
            font-size: 0.833vw;
            line-height: 1.042vw;
            color: #0d0a19;
            @media (min-width: 1440px) {
              font-size: 12px;
              line-height: 19px;
            }
          }
        }
      }

      .list {
        display: flex;
        flex-direction: column;
        gap: 1.319vw;
        margin-top: 1.667vw;
        .list-item {
          display: flex;
          justify-content: space-between;
          align-items: center;

          input {
            cursor: pointer;
            border: 0.069vw solid #e4e6e8;
            border-radius: 0.139vw;
            width: ${desktopSizes._20px};
            height: ${desktopSizes._20px};
            @media (min-width: 1440px) {
              width: 20px;
              height: 20px;
            }
          }

          label {
            cursor: pointer;
            font-family: ${(props) =>
              props.language == "en" ? "Poppins" : "Almarai"};
            font-style: normal;
            font-weight: ${(props) => (props.language == "en" ? "500" : "400")};
            font-size: 1.111vw;
            line-height: 1.667vw;
            color: #0d0a19;

            @media (min-width: 1440px) {
              font-size: 16px;
              line-height: 24px;
            }
          }
          .capacity-label {
            direction: ltr;
            font-family: "Poppins";
            font-weight: 500;
          }
          .brand {
            display: flex;
            align-items: center;
            gap: 1.111vw;

            img {
              width: 2.222vw;
              height: 2.222vw;
              object-fit: contain;

              @media (min-width: 1440px) {
                width: 32px;
                height: 32px;
              }
            }
          }
        }
      }
      // .brand-list {
      //   // height: ${(props) => (props.collapseBrands ? "22.57vw" : "auto")};
      //   // overflow: hidden;
      // }
    }
    .filter-section:last-of-type {
      border: 0;
      padding: 0 1.111vw;
    }
  }
  .btn-container {
    padding: 0.556vw 1.111vw;
    background: #ffffff;
    .submit-btn {
      width: 22.6vw;
      height: 3.194vw;
      cursor: pointer;
      border: none;
      background: #154cd6;
      color: #ffffff;
      border-radius: 0.278vw;
      font-family: "Poppins";
      font-style: normal;
      font-weight: 600;
      font-size: 1.111vw;
      line-height: 1.458vw;

      @media (min-width: 1440px) {
        font-size: 16px;
        line-height: 21px;
      }
    }
  }

  @media (max-width: 768px) {
    width: 91.467vw;
    border: ${mobileSizes._1px} solid #e4e6e8;
    border-radius: ${mobileSizes._4px};
    padding: ${mobileSizes._32px} 0 ${mobileSizes._24px} 0;
    .sidebar-header {
      padding: 0 ${mobileSizes._16px};
      margin-bottom: ${mobileSizes._16px};
      display: flex;
      justify-content: space-between;
      align-items: center;
      .title {
        font-weight: 600;
        font-size: ${mobileSizes._16px};
        line-height: ${mobileSizes._32px};
      }

      .clear {
        font-family: "Inter";
        font-style: normal;
        font-weight: 600;
        font-size: ${mobileSizes._16px};
        line-height: ${mobileSizes._32px};
      }
    }
    .filters {
      height: auto;
      gap: ${mobileSizes._24px};
      margin-bottom: 0;

      .filter-section {
        padding: 0 ${mobileSizes._16px} ${mobileSizes._24px}
          ${mobileSizes._16px};
        border-bottom: ${mobileSizes._1px} solid #e4e6e8;
        .filter-title {
          font-size: ${mobileSizes._14px};
          line-height: ${mobileSizes._32px};
        }
        .search-form {
          margin-top: ${mobileSizes._16px};
          .search-input {
            width: 82.933vw;
            height: 16.533vw;
            border: ${mobileSizes._1px} solid #e4e6e8;
            border-radius: ${mobileSizes._8px};
            font-size: ${mobileSizes._16px};
            line-height: ${mobileSizes._32px};
            padding: ${mobileSizes._16px};
            padding-right: 12.8vw;
          }
          .search-input::placeholder {
            font-size: ${mobileSizes._16px};
            line-height: ${mobileSizes._32px};
          }
          .search {
            left: ${(props) => props.language == "en" && "72.267vw"};
            right: ${(props) => props.language == "ar" && "72.267vw"};
            top: ${mobileSizes._19px};
            svg {
              width: ${mobileSizes._24px};
              height: ${mobileSizes._24px};
            }
          }
        }
        .view-all {
          margin-top: ${mobileSizes._24px};
          font-family: "Inter";
          font-style: normal;
          font-weight: 600;
          font-size: ${mobileSizes._16px};
          line-height: ${mobileSizes._19px};
        }
        .price-inputs {
          gap: ${mobileSizes._16px};
          margin-top: ${mobileSizes._16px};
          .input-div {
            input {
              border: ${mobileSizes._1px} solid #e4e6e8;
              border-radius: ${mobileSizes._8px};
              width: 39.2vw;
              height: 16.533vw;
              padding: ${(props) =>
                props.language == "en"
                  ? `${mobileSizes._21px} 13.333vw ${mobileSizes._21px}
              ${mobileSizes._16px}`
                  : `  ${mobileSizes._21px}  ${mobileSizes._16px}  ${mobileSizes._21px}
                 13.333vw`};
              font-size: ${mobileSizes._16px};
              line-height: ${mobileSizes._19px};
            }
            input::placeholder {
              font-weight: 400;
              font-size: ${mobileSizes._16px};
              line-height: ${mobileSizes._19px};
            }

            input::-webkit-outer-spin-button,
            input::-webkit-inner-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }

            /* Firefox */
            input[type="number"] {
              -moz-appearance: textfield;
            }
            label {
              position: absolute;
              left: ${(props) => props.language == "en" && "28.533vw"};
              right: ${(props) => props.language == "ar" && "28.533vw"};
              top: ${mobileSizes._24px};
              font-weight: 500;
              font-size: ${mobileSizes._12px};
              line-height: ${mobileSizes._15px};
            }
          }
        }

        .list {
          gap: ${mobileSizes._19px};
          margin-top: ${mobileSizes._24px};
          .list-item {
            input[type="checkbox"] {
              border: ${mobileSizes._1px} solid #e4e6e8;
              border-radius: ${mobileSizes._2px};
              width: ${mobileSizes._20px};
              height: ${mobileSizes._20px};
            }

            label {
              font-size: ${mobileSizes._16px};
              line-height: ${mobileSizes._24px};
            }
            .brand {
              display: flex;
              align-items: center;
              gap: ${mobileSizes._12px};

              img {
                width: ${mobileSizes._24px};
                height: ${mobileSizes._18px};
                object-fit: contain;
              }
            }
          }
        }
        // .brand-list {
        //   // height: ${(props) =>
          props.collapseBrands ? "22.57vw" : "auto"};
        //   // overflow: hidden;
        // }
      }
      .filter-section:last-of-type {
        border: 0;
        padding: 0 ${mobileSizes._16px};
      }
    }
    .btn-container {
      padding: 0.556vw 1.111vw;
      background: #ffffff;
      .submit-btn {
        width: 22.6vw;
        height: 3.194vw;
        cursor: pointer;
        border: none;
        background: #154cd6;
        color: #ffffff;
        border-radius: 0.278vw;
        font-family: "Poppins";
        font-style: normal;
        font-weight: 600;
        font-size: 1.111vw;
        line-height: 1.458vw;
      }
    }
  }
`;

export default SidebarFilter;
